import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './api'
import type { DateRange, Site } from './queries'

export type SiteDataState = 'not-connected' | 'backfilling' | 'ready'

export interface Metrics {
  clicks: number
  impressions: number
  /** Null rather than 0 when there were no impressions: a rate with no
   * denominator is unknown, and rendering it as 0% is a lie. */
  ctr: number | null
  position: number | null
}

export interface MetricsRow extends Metrics {
  previous: Metrics | null
  /** current - previous. Null unless the request asked to compare. */
  delta: Metrics | null
}

export interface Coverage {
  clicks: number | null
  impressions: number | null
}

export interface SyncSeries {
  dimensionSet: string
  searchType: string
  earliestDate: string | null
  latestDate: string | null
  backfillCursor: string | null
  backfillDone: string | null
  lastRunAt: string | null
  lastError: string | null
}

export interface Envelope {
  state: SiteDataState
  site: { id: string; host: string; displayName: string; parentSiteId: string | null }
  property: {
    id: string
    uri: string
    type: 'url-prefix' | 'sc-domain'
    permissionLevel: string
    searchTypes: string[]
    linkedAt: string
    disappearedAt: string | null
    connection: {
      id: string
      email: string
      invalidatedAt: string | null
      invalidatedReason: string | null
      lastSyncedAt: string | null
    }
  } | null
  searchType: string
  range: { from: string; to: string }
  previousRange: { from: string; to: string } | null
}

export interface Paging {
  limit: number
  offset: number
  sort: string
  direction: 'asc' | 'desc' | null
  search: string | null
}

export type QueryRow = MetricsRow & { queryId: number; query: string }
export type PageRow = MetricsRow & { pageId: number; url: string }

/** Which engine a keyword or page table is read from; `all` adds the two. */
export const ENGINE_FILTERS = ['all', 'google', 'bing'] as const
export type EngineFilter = (typeof ENGINE_FILTERS)[number]

export interface Breakdown<Row> extends Envelope {
  /** What was actually read — Google alone when the site has no Bing link. */
  engine: EngineFilter
  /** What the site can be read from, so a choice is only offered where there is one. */
  engines: ('google' | 'bing')[]
  rows: Row[]
  total: number
  paging: Paging
  topline: MetricsRow
  dimensionTotals: Metrics
  coverage: Coverage
}

export interface SeriesRow extends Metrics {
  date: string
}

/** A single keyword, with everything the detail page draws. `topline` is the
 * site's complete series for the same window, so a share can be stated against
 * what happened rather than against what Google named. */
export interface KeywordDetail extends Envelope {
  query: { id: number; text: string }
  totals: MetricsRow
  topline: MetricsRow
  series: SeriesRow[]
  previousSeries: SeriesRow[]
  pages: { rows: PageRow[]; total: number; dimensionTotals: Metrics }
  related: { rows: RelatedQueryRow[]; total: number }
}

/** A neighbour, with how many of this row's pages (or keywords) it shares. */
export type RelatedQueryRow = QueryRow & { shared: number }
export type RelatedPageRow = PageRow & { shared: number }

/** What the sitemap crawl knows about one url. No bucket here says "indexed"
 * either — see the coverage report. */
export interface PageSitemapFacts {
  declared: boolean
  everDeclared: boolean
  documents: string[]
  lastmod: string | null
  changefreq: string | null
  priority: number | null
  removedAt: string | null
  blockedByRobots: boolean
}

export interface PageDetail extends Envelope {
  page: {
    id: number
    url: string
    /** Which feed interned the url, so a page nothing has ever ranked for can
     * still say where it came from. */
    firstSeenVia: 'search' | 'sitemap'
    firstSeenAt: string
  }
  sitemap: PageSitemapFacts
  totals: MetricsRow
  topline: MetricsRow
  series: SeriesRow[]
  previousSeries: SeriesRow[]
  keywords: { rows: QueryRow[]; total: number; dimensionTotals: Metrics }
  related: { rows: RelatedPageRow[]; total: number }
}

/**
 * A detail page's list once someone has asked past the first five. It is not a
 * `Breakdown`: these lists have no sort and no filter of their own, so a page
 * is all there is to carry.
 */
export interface DetailList<Row> extends Envelope {
  rows: Row[]
  total: number
  paging: { limit: number; offset: number }
}

/**
 * The bands a keyword can be read against, named for where they put it. Each
 * one implies its own target — the next tier up — because the whole report is
 * about what a small push is worth, not about reaching first place.
 */
export const STRIKING_BANDS = [
  { key: 'page-two', label: 'Page two', min: 11, max: 20, target: 'the bottom of page one' },
  { key: 'page-one', label: 'Bottom of page one', min: 4, max: 10, target: 'the top three' },
  { key: 'page-three', label: 'Page three', min: 21, max: 30, target: 'page one' },
] as const

export type StrikingBand = (typeof STRIKING_BANDS)[number]['key']

export const DEFAULT_STRIKING_BAND: StrikingBand = 'page-two'

export function strikingBand(key: StrikingBand) {
  return STRIKING_BANDS.find((band) => band.key === key) ?? STRIKING_BANDS[0]
}

/** The site's own page that answers a keyword, and how alone it is in doing so.
 * `pages` above one with a low `share` is two of the site's urls competing. */
export interface KeywordPage {
  pageId: number
  url: string
  impressions: number
  share: number
  pages: number
}

export interface StrikingRow extends MetricsRow {
  queryId: number
  query: string
  /** Clicks on top of what it earns now, if it reached the target band. A floor:
   * impressions below the first page are only counted when someone looks that
   * far, so moving up raises the denominator too and this does not count it.
   * Null when the site has no measurable click rate at the target positions. */
  uplift: number | null
  page: KeywordPage | null
}

export interface StrikingData extends Envelope {
  rows: StrikingRow[]
  total: number
  uplift: number | null
  band: { min: number; max: number }
  /** Where the uplift is measured to, and what the site earns there today. */
  target: { from: number; to: number; ctr: number | null; keywords: number }
  paging: { limit: number; offset: number }
}

/** Where a keyword sits, by its weighted average position over the window. */
export const KEYWORD_BANDS = ['top-3', 'top-10', 'top-20', 'beyond'] as const

export type KeywordBand = (typeof KEYWORD_BANDS)[number]

export interface KeywordBandRow {
  band: KeywordBand
  keywords: number
  clicks: number
}

export interface OverviewData extends Envelope {
  totals: MetricsRow
  series: SeriesRow[]
  /** The comparison window on its own dates; the chart realigns it. */
  previousSeries: SeriesRow[]
  /** Empty rather than absent when nothing is connected, so the panel can say
   * so without checking `state` twice. */
  keywords: KeywordBandRow[]
  /** Null when the site has no Bing link; the page then draws Google alone. */
  bing: BingOverview | null
  sync: { backfilling: boolean; dimensionSets: SyncSeries[] }
}

/** Bing over the same window. `position` is always null: Bing reports none at
 * site level. */
export interface BingOverview {
  totals: MetricsRow
  series: SeriesRow[]
  previousSeries: SeriesRow[]
}

export interface GeoData extends Envelope {
  countries: {
    rows: (MetricsRow & { country: string })[]
    total: number
    dimensionTotals: Metrics
  }
  devices: (MetricsRow & { device: 'desktop' | 'mobile' | 'tablet' })[]
  topline: MetricsRow
  coverage: Coverage
}

/**
 * Google's current opinion of one url. It has no date range and no series — it
 * is whatever the last inspection returned, which is why `inspectedAt` travels
 * with it and the ui always shows how old it is.
 */
export interface PageInspection {
  pageId: number
  verdict: string | null
  coverageState: string | null
  robotsTxtState: string | null
  indexingState: string | null
  pageFetchState: string | null
  googleCanonical: string | null
  userCanonical: string | null
  lastCrawlTime: string | null
  crawledAs: string | null
  sitemaps: string[]
  referringUrls: string[]
  inspectedAt: string
}

export interface PageInspectionData {
  page: { id: number; url: string }
  inspection: PageInspection | null
}

/**
 * The two feeds reconciled, per page. Named after what can be demonstrated —
 * the site declares a url, search has shown it — rather than after "indexed",
 * which is a separate and much scarcer answer.
 */
export const COVERAGE_STATUSES = [
  'declared-and-seen',
  'declared-not-seen',
  'seen-undeclared',
  'dropped',
] as const

export type CoverageStatus = (typeof COVERAGE_STATUSES)[number]

export interface CoverageRow {
  pageId: number
  url: string
  status: CoverageStatus
  lastmod: string | null
  clicks: number
  impressions: number
  blockedByRobots: boolean
}

export interface CoverageData extends Envelope {
  rows: CoverageRow[]
  total: number
  facets: { status: CoverageStatus; count: number }[]
  paging: { limit: number; offset: number }
}

export interface SitemapDocument {
  id: string
  url: string
  kind: 'index' | 'urlset' | 'text' | 'unknown'
  discoveredVia: 'robots' | 'search-console' | 'probe' | 'index'
  parentSitemapId: string | null
  urlCount: number
  httpStatus: number | null
  lastFetchedAt: string | null
  lastChangedAt: string | null
  lastError: string | null
}

export interface SitemapsData extends Envelope {
  inventory: {
    robots: {
      resolvedOrigin: string | null
      httpStatus: number | null
      fetchedAt: string | null
      declared: string[]
      lastError: string | null
    } | null
    documents: SitemapDocument[]
    declaredUrls: number
  }
}

/**
 * The API defaults its window to the newest day it actually holds rather than
 * today, so ranges are sent as a day count and the server anchors them. That
 * keeps a site whose sync is a few days behind from opening on an empty chart.
 */
const RANGE_DAYS: Record<DateRange, number> = {
  '7d': 7,
  '28d': 28,
  '3m': 90,
  '12m': 365,
  '16m': 485,
}

export function rangeDays(range: DateRange): number {
  return RANGE_DAYS[range]
}

/** `to` is deliberately omitted so the server anchors on its own newest day. */
function rangeParams(range: DateRange) {
  return { days: String(RANGE_DAYS[range]), compare: 'true' } as const
}

export interface SortState {
  sort: 'clicks' | 'impressions' | 'ctr' | 'position'
  direction: 'asc' | 'desc'
}

export interface TableState extends SortState {
  limit: number
  offset: number
  search: string
}

export const DEFAULT_TABLE: TableState = {
  sort: 'clicks',
  direction: 'desc',
  limit: 25,
  offset: 0,
  search: '',
}

function breakdownParams(range: DateRange, table: TableState) {
  return {
    ...rangeParams(range),
    sort: table.sort,
    direction: table.direction,
    limit: String(table.limit),
    offset: String(table.offset),
    ...(table.search ? { search: table.search } : {}),
  }
}

/** Shared across every section so a range change does not blank the page. */
const shared = {
  placeholderData: keepPreviousData,
  staleTime: 30_000,
} as const

export function useOverview(site: Site | undefined, range: DateRange) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'overview', range],
    enabled: Boolean(site),
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].overview.$get({
        param: { siteId: site?.id ?? '' },
        query: rangeParams(range),
      })
      return unwrap<OverviewData>(response)
    },
  })
}

export function useQueriesBreakdown(
  site: Site | undefined,
  range: DateRange,
  table: TableState,
  engine: EngineFilter,
) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'queries', range, table, engine],
    enabled: Boolean(site),
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].queries.$get({
        param: { siteId: site?.id ?? '' },
        query: { ...breakdownParams(range, table), engine },
      })
      return unwrap<Breakdown<QueryRow>>(response)
    },
  })
}

/**
 * The keywords worth pushing. Ordered by the server, and deliberately not
 * sortable: the ordering is the answer the page exists to give.
 */
export function useOpportunities(
  site: Site | undefined,
  range: DateRange,
  band: StrikingBand,
  page: { limit: number; offset: number },
) {
  const bounds = strikingBand(band)
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'opportunities', range, band, page.offset, page.limit],
    enabled: Boolean(site),
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].opportunities.$get({
        param: { siteId: site?.id ?? '' },
        query: {
          ...rangeParams(range),
          min: String(bounds.min),
          max: String(bounds.max),
          limit: String(page.limit),
          offset: String(page.offset),
        },
      })
      return unwrap<StrikingData>(response)
    },
  })
}

export function usePagesBreakdown(
  site: Site | undefined,
  range: DateRange,
  table: TableState,
  engine: EngineFilter,
) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'pages', range, table, engine],
    enabled: Boolean(site),
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].pages.$get({
        param: { siteId: site?.id ?? '' },
        query: { ...breakdownParams(range, table), engine },
      })
      return unwrap<Breakdown<PageRow>>(response)
    },
  })
}

/** One keyword's own page. Everything it draws arrives together: the range and
 * the primary property are resolved once per request, so three calls would pay
 * for that three times over. */
export function useKeywordDetail(site: Site | undefined, range: DateRange, queryId: number) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'keyword', range, queryId],
    enabled: Boolean(site) && Number.isFinite(queryId),
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].queries[':queryId'].$get({
        param: { siteId: site?.id ?? '', queryId: String(queryId) },
        query: rangeParams(range),
      })
      return unwrap<KeywordDetail>(response)
    },
  })
}

export function usePageDetail(site: Site | undefined, range: DateRange, pageId: number) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'page', range, pageId],
    enabled: Boolean(site) && Number.isFinite(pageId),
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].pages[':pageId'].$get({
        param: { siteId: site?.id ?? '', pageId: String(pageId) },
        query: rangeParams(range),
      })
      return unwrap<PageDetail>(response)
    },
  })
}

/**
 * The rest of a detail page's list, fetched only once someone asks for it. The
 * first five arrive with the page — these four exist so the sixth does not cost
 * a second resolution of the property and the window on every visit.
 */
export const DETAIL_PAGE_SIZE = 10

function listParams(range: DateRange, offset: number) {
  return { ...rangeParams(range), limit: String(DETAIL_PAGE_SIZE), offset: String(offset) }
}

export function useKeywordPages(
  site: Site | undefined,
  range: DateRange,
  queryId: number,
  offset: number,
  enabled: boolean,
) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'keyword', range, queryId, 'pages', offset],
    enabled: Boolean(site) && Number.isFinite(queryId) && enabled,
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].queries[':queryId'].pages.$get({
        param: { siteId: site?.id ?? '', queryId: String(queryId) },
        query: { ...listParams(range, offset), sort: 'clicks', direction: 'desc' },
      })
      return unwrap<DetailList<PageRow> & { dimensionTotals: Metrics }>(response)
    },
  })
}

export function useKeywordRelated(
  site: Site | undefined,
  range: DateRange,
  queryId: number,
  offset: number,
  enabled: boolean,
) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'keyword', range, queryId, 'related', offset],
    enabled: Boolean(site) && Number.isFinite(queryId) && enabled,
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].queries[':queryId'].related.$get({
        param: { siteId: site?.id ?? '', queryId: String(queryId) },
        query: listParams(range, offset),
      })
      return unwrap<DetailList<RelatedQueryRow>>(response)
    },
  })
}

export function usePageKeywords(
  site: Site | undefined,
  range: DateRange,
  pageId: number,
  offset: number,
  enabled: boolean,
) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'page', range, pageId, 'keywords', offset],
    enabled: Boolean(site) && Number.isFinite(pageId) && enabled,
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].pages[':pageId'].queries.$get({
        param: { siteId: site?.id ?? '', pageId: String(pageId) },
        query: { ...listParams(range, offset), sort: 'clicks', direction: 'desc' },
      })
      return unwrap<DetailList<QueryRow> & { dimensionTotals: Metrics }>(response)
    },
  })
}

export function usePageRelated(
  site: Site | undefined,
  range: DateRange,
  pageId: number,
  offset: number,
  enabled: boolean,
) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'page', range, pageId, 'related', offset],
    enabled: Boolean(site) && Number.isFinite(pageId) && enabled,
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].pages[':pageId'].related.$get({
        param: { siteId: site?.id ?? '', pageId: String(pageId) },
        query: listParams(range, offset),
      })
      return unwrap<DetailList<RelatedPageRow>>(response)
    },
  })
}

export function useGeo(site: Site | undefined, range: DateRange) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'geo', range],
    enabled: Boolean(site),
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].geo.$get({
        param: { siteId: site?.id ?? '' },
        query: { ...rangeParams(range), limit: '8' },
      })
      return unwrap<GeoData>(response)
    },
  })
}

export function usePageInspection(
  site: Site | undefined,
  pageId: number,
  opts: { enabled: boolean; poll: boolean },
) {
  return useQuery({
    queryKey: ['site-data', site?.id, 'inspection', pageId],
    enabled: Boolean(site) && opts.enabled,
    // An inspection only changes when someone spends quota asking for one, so
    // the cached answer never goes stale on its own. The job replies on a
    // queue rather than to the request that started it, which leaves looking
    // again as the only way to notice — and only while someone is waiting.
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: opts.poll ? 3000 : false,
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].pages[':pageId'].inspection.$get({
        param: { siteId: site?.id ?? '', pageId: String(pageId) },
      })
      return unwrap<PageInspectionData>(response)
    },
  })
}

/** Enqueues one inspection. The 202 says the job was accepted, never that the
 * result is ready — the caller watches the query above for that. */
export function useInspectPage(site: Site | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (pageId: number) => {
      const response = await api.api['site-data'][':siteId'].pages[':pageId'].inspect.$post({
        param: { siteId: site?.id ?? '', pageId: String(pageId) },
      })
      return unwrap<{ queued: boolean }>(response)
    },
    onSuccess: (_result, pageId) => {
      queryClient.invalidateQueries({ queryKey: ['site-data', site?.id, 'inspection', pageId] })
    },
  })
}

/**
 * The reconciliation. `status` narrows to one bucket for the drill-down; the
 * facet counts come back either way, so opening a bucket never costs a second
 * request to keep the totals on screen.
 */
export function useCoverage(
  site: Site | undefined,
  range: DateRange,
  opts: { status?: CoverageStatus; limit?: number; enabled?: boolean } = {},
) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'coverage', range, opts.status ?? null, opts.limit ?? 0],
    enabled: Boolean(site) && opts.enabled !== false,
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].coverage.$get({
        param: { siteId: site?.id ?? '' },
        query: {
          ...rangeParams(range),
          limit: String(opts.limit ?? 8),
          ...(opts.status ? { status: opts.status } : {}),
        },
      })
      return unwrap<CoverageData>(response)
    },
  })
}

/** What the crawl found, so an empty inventory can be told apart from a broken
 * one: a sitemap that 404s and a site that never had one look identical from
 * the page counts alone. */
export function useSitemaps(site: Site | undefined, range: DateRange) {
  return useQuery({
    ...shared,
    queryKey: ['site-data', site?.id, 'sitemaps', range],
    enabled: Boolean(site),
    queryFn: async () => {
      const response = await api.api['site-data'][':siteId'].sitemaps.$get({
        param: { siteId: site?.id ?? '' },
        query: rangeParams(range),
      })
      return unwrap<SitemapsData>(response)
    },
  })
}
